export interface InsightArticle {
  id: string;
  slug: string;
  category: string;
  title: string;
  subtitle: string;
  excerpt: string;
  coverImg: string;
  author: string;
  authorRole: string;
  authorAvatar: string;
  readTime: string;
  publishDate: string;
  tags: string[];
  featured?: boolean;
  sections: {
    heading: string;
    body: string;
  }[];
}

export interface InsightCategory {
  key: string;
  label: string;
}

export const insightCategories: InsightCategory[] = [
  { key: 'all', label: '全部文章' },
  { key: 'strategy', label: '空間策略' },
  { key: 'design', label: '設計觀點' },
  { key: 'budget', label: '預算規劃' },
  { key: 'renovation', label: '翻新改造' },
  { key: 'brand', label: '品牌空間' },
];

export const insightArticles: InsightArticle[] = [
  {
    id: 'insight-1',
    slug: 'why-commercial-space-strategy-first',
    category: 'strategy',
    title: '為什麼商業空間要先策略，再設計？',
    subtitle: '許多業主花大錢裝修，卻沒有達到預期的效果，問題往往不在設計本身',
    excerpt: '一個讓人流連忘返的商業空間，絕不只是「好看」而已。在動工之前，你真的想清楚了嗎？這篇文章拆解商業空間策略與美感設計的本質差異，帶你重新認識一個成功商業空間的完整邏輯。',
    coverImg: 'https://readdy.ai/api/search-image?query=modern%20commercial%20interior%20design%20strategy%20planning%20session%20with%20architectural%20blueprints%20and%20material%20samples%20on%20a%20clean%20wooden%20table%2C%20warm%20natural%20light%2C%20minimalist%20design%20studio%2C%20beige%20and%20warm%20tones%2C%20professional%20atmosphere%2C%20no%20people%2C%20ultra%20sharp&width=800&height=500&seq=insight-cover-1&orientation=landscape',
    author: '林佳穎',
    authorRole: '首席策略顧問',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20female%20portrait%20headshot%20warm%20neutral%20background%20confident%20designer%20taiwanese%20woman%2030s%20clean%20minimal%20style&width=80&height=80&seq=author-avatar-1&orientation=squarish',
    readTime: '6 分鐘',
    publishDate: '2024-11-15',
    tags: ['策略規劃', '商業空間', '設計思維'],
    featured: true,
    sections: [
      {
        heading: '大多數業主在問錯了問題',
        body: '「你們的設計費怎麼算？」「可以幫我做一個日式風格的嗎？」這是我們在初次諮詢時最常聽到的兩個問題。這兩個問題本身沒有錯，但如果這就是業主在裝修前唯一思考的方向，往往就是翻新後業績沒有起色、甚至更差的根本原因。\n\n商業空間設計和住宅設計最大的差異在於：住宅的核心是「讓居住者舒適」，而商業空間的核心是「讓顧客消費、讓品牌產生價值」。這兩件事，表面上都是讓人覺得「好」，但背後的邏輯完全不同。',
      },
      {
        heading: '空間策略，到底是在解決什麼問題？',
        body: '策略是在設計之前，先回答這些問題：你的目標客群是誰？他們在什麼情境下會進你的店？你希望他們在店裡停留多久？你最想要他們購買哪些品項？\n\n當你能清楚回答這些問題，設計方向自然就會浮現。舉個例子：同樣是咖啡廳，主打「一人工作空間」的咖啡廳和主打「親子下午茶」的咖啡廳，在座位排列、照明設計、噪音控制、動線規劃上，幾乎是完全相反的答案。\n\n如果你不先問「這個空間要服務誰、達成什麼目標」，任何設計師給你的方案都只是猜測。',
      },
      {
        heading: '策略先行的三個實際好處',
        body: '一、省錢：當目標明確，每一分錢的花法都有根據，不會因為「感覺不錯」而追加預算。\n\n二、省時：設計方向確定後，修改次數大幅減少，從提案到定案的週期可以縮短 40% 以上。\n\n三、效果看得見：空間開幕後，有明確目標就有評估標準。我們曾經服務的一位診所業主，在策略調整後三個月內，初診預約量成長了 65%。',
      },
      {
        heading: '你可以這樣開始',
        body: '在聯繫任何設計公司之前，試著回答這幾個問題：\n\n① 我的目標客群是什麼樣的人？（年齡、消費習慣、來店動機）\n② 我希望顧客進門後的第一個感受是什麼？\n③ 我最希望透過這次裝修解決什麼問題？（業績下滑、老舊形象、坪效太差）\n④ 我的預算能讓我完成多少目標？（部分先做？全部一次到位？）\n\n帶著這些思考來諮詢，你會得到更有價值的建議，也更能判斷設計公司是否真的理解你的需求。',
      },
    ],
  },
  {
    id: 'insight-2',
    slug: 'restaurant-design-mistakes',
    category: 'design',
    title: '餐廳裝修常犯的 5 個設計錯誤，你中了幾個？',
    subtitle: '從動線死角到照明失誤，這些細節決定了顧客願不願意再來',
    excerpt: '餐廳的翻桌率、客單價、回頭率，很大程度上都被空間設計悄悄影響著。我們整理了過去服務 60+ 間餐廳後，最常發現的設計失誤，讓你在裝修前先避開這些坑。',
    coverImg: 'https://readdy.ai/api/search-image?query=stylish%20modern%20restaurant%20interior%20warm%20ambient%20lighting%20intimate%20dining%20setting%20wooden%20tables%20carefully%20arranged%20flowers%20minimalist%20japanese%20influence%20cozy%20atmosphere%20professional%20food%20photography%20style%20no%20people&width=800&height=500&seq=insight-cover-2&orientation=landscape',
    author: '陳柏宇',
    authorRole: '空間設計總監',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20male%20portrait%20headshot%20warm%20background%20taiwanese%20man%2030s%20designer%20confident%20clean%20neutral%20style&width=80&height=80&seq=author-avatar-2&orientation=squarish',
    readTime: '8 分鐘',
    publishDate: '2024-10-28',
    tags: ['餐廳設計', '動線規劃', '照明設計'],
    featured: false,
    sections: [
      {
        heading: '錯誤一：忽略廚房與用餐區的動線衝突',
        body: '許多業主在選址時沒有充分考慮廚房出口與顧客動線的關係。當服務生端著熱食穿越顧客密集區域，不但影響用餐體驗，也存在安全風險。正確的做法是在平面規劃階段就確認服務動線（廚房→出餐口→桌位）與顧客動線（入口→座位→洗手間→出口）不交叉，或盡量減少交叉點。',
      },
      {
        heading: '錯誤二：照明只考慮「夠亮」，忘了氛圍',
        body: '餐廳照明的核心任務不是讓空間亮，而是讓食物好看、讓顧客放鬆。色溫 2700–3000K 的暖白光是餐廳的基本配備，搭配桌面重點照明（可以讓食物色澤更飽滿），輔以低照度的環境光製造溫度感。我們看過太多餐廳用冷白日光燈，讓原本精心設計的料理顯得蒼白無力。',
      },
      {
        heading: '錯誤三：座位密度過高，追求坪效卻犧牲了體驗',
        body: '坪效很重要，但過度壓縮座位間距的後果，是讓顧客不願久待，也不願再來。一般來說，餐廳相鄰桌椅的最小間距為 80cm（可通行），舒適間距為 100–120cm。我們建議在規劃時先確認「舒適入座率」，再計算實際可容納人數，而不是反過來操作。',
      },
      {
        heading: '錯誤四：缺乏視覺焦點，整個空間看起來「很平』',
        body: '進入一個沒有視覺焦點的餐廳，顧客的目光無處安放，空間感覺空洞、缺乏記憶點。好的餐廳設計通常會設計 1–2 個「打卡牆」或視覺亮點，可以是特殊材質的主題牆、懸掛式藝術裝置、或一個有故事感的展示區。這些元素同時也是社群媒體的天然曝光點。',
      },
      {
        heading: '錯誤五：忽略聲學設計，讓噪音破壞用餐氛圍',
        body: '這是最容易被忽視卻影響體驗最深的一項。硬質地板、高天花、大量玻璃帷幕組合在一起，會讓整個空間的迴響時間過長，顧客必須大聲說話才能溝通，進而形成惡性循環。在材料選擇上，適度加入吸音材料（布質椅背、木格柵、植栽牆）可以有效改善聲學環境，讓顧客感受到「安靜的舒適感」。',
      },
    ],
  },
  {
    id: 'insight-3',
    slug: 'retail-space-layout-guide',
    category: 'strategy',
    title: '零售門市的空間佈局指南：讓顧客自然走向高毛利區',
    subtitle: '科學與美感的結合，讓你的商品陳列真正影響購買決策',
    excerpt: '為什麼超市的麵包區永遠在最裡面？為什麼高端精品店的入口處永遠有大量空白？這些都不是巧合，是精心設計的空間策略。把這些邏輯用在你的零售門市，轉換率立刻不同。',
    coverImg: 'https://readdy.ai/api/search-image?query=elegant%20modern%20retail%20boutique%20store%20interior%20with%20carefully%20curated%20product%20displays%20on%20minimal%20wooden%20shelves%2C%20warm%20accent%20lighting%2C%20clean%20white%20walls%2C%20premium%20shopping%20experience%2C%20lifestyle%20products%2C%20no%20people%2C%20high-end%20atmosphere&width=800&height=500&seq=insight-cover-3&orientation=landscape',
    author: '林佳穎',
    authorRole: '首席策略顧問',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20female%20portrait%20headshot%20warm%20neutral%20background%20confident%20designer%20taiwanese%20woman%2030s%20clean%20minimal%20style&width=80&height=80&seq=author-avatar-1&orientation=squarish',
    readTime: '7 分鐘',
    publishDate: '2024-10-10',
    tags: ['零售空間', '商品陳列', '動線規劃'],
    featured: false,
    sections: [
      {
        heading: '入口區：減壓帶的重要性',
        body: '許多零售業主恨不得在入口處塞滿商品，但研究顯示，顧客在進入陌生空間的前 3–5 秒是「心理減壓期」，這段時間他們的注意力在適應環境，而不在商品上。留白、簡潔的入口設計，反而能讓顧客更快進入購物狀態。',
      },
      {
        heading: '主動線設計：讓腳步帶著眼睛走',
        body: '大多數人進入空間後會自然向右轉，這是空間規劃中的「右轉原則」。了解這個行為模式後，你就可以將最重要的品類或主打商品放在右側主動線上，確保每個進店的顧客都能「順路」看到它們。主動線的寬度建議不低於 120cm，讓顧客有足夠空間放鬆地瀏覽，不會因為空間壓迫感而想快速離開。',
      },
      {
        heading: '高毛利商品的黃金陳列位置',
        body: '「眼平線是金，腰平線是銀，腳平線是銅」。商品的視線高度直接影響被拿取的機率。你最想賣的商品，應該放在顧客眼平線（130–160cm）到腰平線（90–110cm）之間的位置，這個區域的觸取率是底層的 3–4 倍。',
      },
    ],
  },
  {
    id: 'insight-4',
    slug: 'renovation-budget-planning',
    category: 'budget',
    title: '老店翻新預算怎麼分配才不浪費？五個關鍵決策點',
    subtitle: '不是每一塊牆都需要重做，聰明分配預算才能達到最大效益',
    excerpt: '老店翻新最常見的錯誤，是把有限的預算分散在所有地方，結果哪裡都沒有改好。這篇文章提供一個實用的預算分配框架，幫你找出最值得投資的區域，讓每一塊錢都用在刀口上。',
    coverImg: 'https://readdy.ai/api/search-image?query=renovation%20planning%20design%20studio%20with%20material%20swatches%20floor%20plans%20budget%20spreadsheets%20on%20a%20light%20wooden%20desk%20architect%20tools%20clean%20minimal%20workspace%20warm%20tones%20no%20people%20professional%20interior%20design&width=800&height=500&seq=insight-cover-4&orientation=landscape',
    author: '吳雅婷',
    authorRole: '專案管理總監',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20female%20portrait%20headshot%20warm%20background%20taiwanese%20woman%2030s%20project%20manager%20friendly%20expression%20clean%20business%20style&width=80&height=80&seq=author-avatar-3&orientation=squarish',
    readTime: '9 分鐘',
    publishDate: '2024-09-20',
    tags: ['預算規劃', '老店翻新', '成本控制'],
    featured: false,
    sections: [
      {
        heading: '先做診斷，再談預算',
        body: '翻新前最重要的一步，是釐清「哪些是顧客真正感受到的問題，哪些只是業主自己覺得不好看」。前者值得優先投資，後者可以暫緩或用低成本方式處理。我們建議在報價前做一次系統性的空間診斷，從顧客的視角評估每個區域的現況。',
      },
      {
        heading: '高 ROI 翻新項目優先',
        body: '根據我們的統計，以下幾個項目的翻新 ROI 最高：① 入口門面改造（品牌識別）、② 主視覺牆（打卡與拍照）、③ 照明系統升級（影響整體氛圍感）、④ 衛生間翻新（顧客評分的高影響因子）。這四個區域往往就能用總預算的 50–60% 達到 80% 的視覺改變效果。',
      },
      {
        heading: '分階段施工的正確做法',
        body: '分階段不代表省了設計費，而是省了「重工費」。正確的分階段應該是：第一階段先完成整體設計規劃（一次到位），第二步再分批施工。這樣才能確保每個階段的施工結果相互協調，不會因為先做了後悔、或後做的和前做的風格不一致。',
      },
    ],
  },
  {
    id: 'insight-5',
    slug: 'clinic-space-trust-design',
    category: 'brand',
    title: '診所醫美空間設計的信任感公式',
    subtitle: '顧客願不願意進來，空間的第一印象決定了 70%',
    excerpt: '在醫療美容這個高度依賴「信任感」的行業，空間設計扮演的角色遠超想像。從色彩心理學到材質選擇，這篇文章拆解一個讓人「感覺專業、放心把自己交給你」的診所空間是怎麼設計出來的。',
    coverImg: 'https://readdy.ai/api/search-image?query=high-end%20medical%20aesthetic%20clinic%20interior%20design%20with%20clean%20white%20walls%20warm%20accent%20lighting%20natural%20materials%20wood%20panels%20green%20plants%20minimal%20luxury%20professional%20calm%20atmosphere%20no%20people%20premium%20healthcare%20space&width=800&height=500&seq=insight-cover-5&orientation=landscape',
    author: '陳柏宇',
    authorRole: '空間設計總監',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20male%20portrait%20headshot%20warm%20background%20taiwanese%20man%2030s%20designer%20confident%20clean%20neutral%20style&width=80&height=80&seq=author-avatar-2&orientation=squarish',
    readTime: '7 分鐘',
    publishDate: '2024-09-05',
    tags: ['診所設計', '醫美空間', '信任感設計'],
    featured: false,
    sections: [
      {
        heading: '信任感的來源：材質、光線、整潔',
        body: '顧客對診所的第一印象，70% 來自空間的「整潔感」與「材質質感」。大量採用啞光石材、原木飾板、霧面金屬五金，搭配色溫 3000K 的暖白光，可以在保有醫療專業感的同時，去除冰冷的「醫院感」，讓顧客在踏入的瞬間就感到放鬆。',
      },
      {
        heading: '等候區：從「等待」變成「享受」',
        body: '診所的等候區往往是最容易讓顧客焦慮的地方。設計重點在於：提供足夠的私密感（每個座位之間有視覺遮擋）、製造環境焦點（精心陳列的植物、書冊、香氛）、保持適當的照明亮度（不過亮、不過暗）。一個讓人感到被款待的等候區，能有效降低顧客的等待焦慮，提升整體服務評分。',
      },
      {
        heading: '品牌視覺的一致性',
        body: '從戶外招牌到接待台、從諮詢室到衛生間，每個觸點都應該傳遞一致的品牌訊息。顏色、字體、材質、香氛、背景音樂，這些細節共同構成了顧客對你品牌的「感受記憶」，而這種記憶是最難被競爭對手複製的競爭優勢。',
      },
    ],
  },
  {
    id: 'insight-6',
    slug: 'office-space-productivity',
    category: 'design',
    title: '辦公空間設計如何提升 30% 工作效率？',
    subtitle: '從座位配置到採光設計，工作環境對生產力的影響超過你的想像',
    excerpt: '好的辦公空間不只是「好看」，而是能讓人進入狀態、深度專注。我們整理了辦公空間設計的核心邏輯，包含採光、聲學、社交區規劃，讓你的工作場域真正支持業務成長。',
    coverImg: 'https://readdy.ai/api/search-image?query=modern%20creative%20office%20workspace%20interior%20design%20open%20plan%20with%20natural%20light%20plants%20minimal%20wooden%20desks%20collaborative%20zones%20warm%20tones%20beige%20accent%20walls%20professional%20productive%20atmosphere%20no%20people%20clean%20design&width=800&height=500&seq=insight-cover-6&orientation=landscape',
    author: '林佳穎',
    authorRole: '首席策略顧問',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20female%20portrait%20headshot%20warm%20neutral%20background%20confident%20designer%20taiwanese%20woman%2030s%20clean%20minimal%20style&width=80&height=80&seq=author-avatar-1&orientation=squarish',
    readTime: '6 分鐘',
    publishDate: '2024-08-18',
    tags: ['辦公空間', '工作效率', '企業設計'],
    featured: false,
    sections: [
      {
        heading: '自然採光的不可取代性',
        body: '研究顯示，有充足自然採光的辦公環境，員工的生產力比純人工照明的環境高出 15–20%。在空間規劃時，應優先將高頻使用的工作區安排在靠窗位置，並使用遮光率適中的窗簾（透光不透視）調節直射光，避免螢幕反光問題。',
      },
      {
        heading: '專注區與協作區的明確分隔',
        body: '一個好的辦公空間應該同時支持「深度工作」和「即興溝通」兩種模式。深度工作區需要安靜、減少干擾，可以透過高隔板、吸音材料、空間位置（遠離主走道）來達成；協作區則需要開放、容易移動，搭配可移動白板和非正式座位，讓創意對話更自然發生。',
      },
    ],
  },
  {
    id: 'insight-7',
    slug: 'brand-space-differentiation',
    category: 'brand',
    title: '打造「無可替代感」：品牌空間差異化的三個維度',
    subtitle: '當你的產品和服務都和對手差不多，空間就是最後一道護城河',
    excerpt: '在競爭激烈的商業市場，純靠產品或價格取勝越來越難。這篇文章從品牌定位出發，探討如何透過空間設計建立獨特的品牌識別，讓顧客一走進來就知道「這個地方和其他家不一樣」。',
    coverImg: 'https://readdy.ai/api/search-image?query=distinctive%20branded%20commercial%20space%20interior%20with%20unique%20design%20elements%20custom%20lighting%20installation%20art%20piece%20as%20focal%20point%20warm%20earthy%20tones%20luxurious%20materials%20memorable%20atmosphere%20no%20people%20editorial%20photography%20style&width=800&height=500&seq=insight-cover-7&orientation=landscape',
    author: '吳雅婷',
    authorRole: '專案管理總監',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20female%20portrait%20headshot%20warm%20background%20taiwanese%20woman%2030s%20project%20manager%20friendly%20expression%20clean%20business%20style&width=80&height=80&seq=author-avatar-3&orientation=squarish',
    readTime: '8 分鐘',
    publishDate: '2024-07-30',
    tags: ['品牌設計', '差異化', '空間識別'],
    featured: false,
    sections: [
      {
        heading: '維度一：感官記憶的塑造',
        body: '人的記憶是多感官的。一個讓你記住的空間，往往不只是視覺上好看，還有特殊的氣味（品牌香氛）、舒適的背景音樂、獨特的觸感（門把的重量感、桌面的溫度）。這些細節單獨看起來微不足道，但組合在一起，就能創造出只屬於你這個品牌的「感官指紋」。',
      },
      {
        heading: '維度二：故事性的空間敘事',
        body: '每個成功的品牌空間都在說一個故事。這個故事可以關於品牌的起源、關於選材的哲學、關於服務的理念。把這個故事轉化成空間中可以「被發現」的設計細節，讓好奇的顧客有探索的樂趣，讓第一次來的人有話題可以跟朋友說。',
      },
      {
        heading: '維度三：稀缺感的設計',
        body: '人對稀缺的事物天生有更強的興趣。訂製元素（非量產的傢具、在地工藝師的手作裝置）、限定材料（稀有石材、特殊木種）、獨特的空間比例（非標準的天花高度、特殊的採光角度），這些元素讓你的空間有「這裡只有這一個」的獨特性，是任何對手都無法直接複製的競爭優勢。',
      },
    ],
  },
  {
    id: 'insight-9',
    slug: 'lighting-design-commercial-space',
    category: 'design',
    title: '照明設計決定空間氣質：商業空間燈光規劃完整指南',
    subtitle: '同樣的材料，燈光對了就值三倍，燈光錯了再貴也廉價',
    excerpt: '照明是商業空間中最容易被低估、卻對整體氛圍影響最深的設計要素。從色溫選擇到層次規劃，這篇文章帶你系統性了解商業照明的邏輯，讓你的空間在燈光下煥發出最真實的品牌質感。',
    coverImg: 'https://readdy.ai/api/search-image?query=dramatic%20commercial%20interior%20lighting%20design%20warm%20accent%20spotlights%20showcasing%20textures%20and%20materials%20in%20an%20elegant%20retail%20or%20restaurant%20space%20moody%20atmospheric%20professional%20photography%20minimal%20no%20people%20wood%20stone%20surfaces%20golden%20light&width=800&height=500&seq=insight-cover-9&orientation=landscape',
    author: '陳柏宇',
    authorRole: '空間設計總監',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20male%20portrait%20headshot%20warm%20background%20taiwanese%20man%2030s%20designer%20confident%20clean%20neutral%20style&width=80&height=80&seq=author-avatar-2&orientation=squarish',
    readTime: '8 分鐘',
    publishDate: '2025-01-20',
    tags: ['照明設計', '燈光規劃', '空間氛圍'],
    featured: false,
    sections: [
      {
        heading: '色溫：決定空間「溫不溫暖」的第一道開關',
        body: '色溫以 K（克耳文）為單位，數字越低越暖、越高越冷。商業空間最常用的三個區間：\n\n2700–3000K（暖黃光）：適合餐廳、酒吧、高端精品店。讓人放鬆、讓食物看起來更美味、讓皮膚色澤更好看。\n\n3000–3500K（自然白）：適合咖啡廳、診所候診區、辦公空間。介於暖與冷之間，清晰不刺眼。\n\n4000K 以上（冷白光）：適合醫療操作區、廚房工作區、需要精準辨色的零售空間。雖然清楚，但長時間使用容易讓空間顯得緊繃冰冷，不建議作為主要氛圍燈使用。',
      },
      {
        heading: '層次照明：三層疊加才是完整的光環境',
        body: '專業的商業照明設計通常由三層構成：\n\n① 基礎照明（Ambient Light）：提供空間整體可見度的底層光，通常由崁燈或間接燈帶提供，避免死角與陰暗感。\n\n② 重點照明（Accent Light）：針對特定物件或區域的強調光，讓商品、藝術品、主題牆「發光」，亮度通常是基礎照明的 3–5 倍。軌道燈是最靈活的選擇。\n\n③ 裝飾照明（Decorative Light）：本身就是視覺元素的燈具，如吊燈、燈條、燈管裝置，為空間增加層次與個性，也是打卡亮點的常見來源。',
      },
      {
        heading: '演色性（CRI）：決定「顏色對不對」',
        body: '演色性指數（CRI）反映燈光在還原物體真實顏色方面的能力，滿分 100 代表與自然光完全一致。\n\n一般商業空間 CRI ≥ 80 即可；餐廳、精品零售、醫美診所建議選用 CRI ≥ 90 的高演色性燈具，確保食物色澤飽滿、布料與石材的紋理質感被完整呈現。\n\n我們在 Horizon 品牌建材展廳的案例中，將主要展示區全面升級為 CRI 97 的燈具後，業主反映顧客對材料質感的評價顯著提升，也讓成交率有所改善。',
      },
      {
        heading: '可調光系統：讓空間隨時段變臉',
        body: '固定亮度的空間是靜止的，可調光系統讓空間能隨著時段、氛圍、活動需求靈活轉換。\n\n早午餐時段可以使用較高亮度配合自然光；晚餐時段調暗整體照明、加強桌面重點燈，立刻營造截然不同的用餐氣氛。\n\n對於診所、高端接待空間，可調光系統也讓不同服務情境（諮詢、操作、休息）有各自適合的燈光場景，提升整體服務的精緻感。初期投資雖然高於固定燈具，但長期帶來的品牌價值與顧客體驗提升，是非常值得的決策。',
      },
    ],
  },
  {
    id: 'insight-8',
    slug: 'renovation-before-after-checklist',
    category: 'renovation',
    title: '老店翻新前必做的 8 件事：避免浪費 50 萬的清單',
    subtitle: '裝修前沒做好功課，很可能在施工時才發現代價高昂的問題',
    excerpt: '老店翻新和新店裝修最大的差別，在於你面對的是一個充滿「已知未知」的現場。管線老化、前裝修層、結構限制……這些問題如果沒有在前期釐清，很可能讓你的翻新預算暴增 30–50%。這篇清單幫你把這些風險前置處理。',
    coverImg: 'https://readdy.ai/api/search-image?query=before%20and%20after%20renovation%20commercial%20space%20inspection%20checklist%20clipboard%20on%20wooden%20table%20renovation%20tools%20architectural%20plans%20warm%20light%20professional%20interior%20assessment%20no%20people%20clean%20aesthetic&width=800&height=500&seq=insight-cover-8&orientation=landscape',
    author: '陳柏宇',
    authorRole: '空間設計總監',
    authorAvatar: 'https://readdy.ai/api/search-image?query=professional%20male%20portrait%20headshot%20warm%20background%20taiwanese%20man%2030s%20designer%20confident%20clean%20neutral%20style&width=80&height=80&seq=author-avatar-2&orientation=squarish',
    readTime: '10 分鐘',
    publishDate: '2024-07-08',
    tags: ['老店翻新', '施工清單', '預算管控'],
    featured: false,
    sections: [
      {
        heading: '清單第 1–3 項：先確認「可不可以動」',
        body: '① 確認現有隔間是否為承重牆（打掉承重牆需要結構補強，費用可達數十萬）。② 確認水電管線的現況與走向（拍照紀錄，避免施工時意外切斷）。③ 確認現有地板下方狀況（是否有架高木地板、是否有管線埋設），這影響你是否能更換地板材料。',
      },
      {
        heading: '清單第 4–6 項：評估「現有的能留多少」',
        body: '④ 評估天花板高度與現有機電設備（空調主機、通風系統）是否可以保留再利用。⑤ 確認現有傢具設備的狀況，能留用的不要輕易全換（節省預算，也減少廢棄物）。⑥ 確認外牆與招牌的申請規範（有些大樓或商圈有設計管制），避免做了之後要拆。',
      },
      {
        heading: '清單第 7–8 項：提前談好「誰來負責」',
        body: '⑦ 與房東確認翻新範圍與還原條款（租約到期時需要將空間恢復原狀的範圍），這直接影響你可以做多深度的更動。⑧ 確認施工期間是否有鄰近商家或住戶的噪音、震動管制，提前安排施工時間，避免糾紛。',
      },
    ],
  },
];
